import React, { FC, PropsWithChildren, useEffect } from 'react';
import { useVkAuthMutation } from '../api/vkAuthApi';
import { ReactComponent as VkIcon } from 'shared/assets/icons/VkIcon.svg';
import { ReactComponent as MailRuIcon } from 'shared/assets/icons/MailRuIcon.svg';
import { ReactComponent as OkIcon } from 'shared/assets/icons/OkIcon.svg';
import { getBoxInfo, getWarehouseInfo } from 'pages/BookingPage';
import { LeadData, useCreateLeadMutation } from 'entities/Lead';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { CLIENT_HOST, VK_AUTH_CLIENT_ID } from 'app/config/envVariables';
import * as VKID from '@vkid/sdk';
import { Button } from 'shared/ui/Button';

interface VkAuthButtonProps extends PropsWithChildren {
  onSuccessAuth: () => void;
  onFailAuth: (error: string) => void;
  className?: string;
}

export const VkAuthButton: FC<VkAuthButtonProps> = (props) => {
  const { onSuccessAuth, onFailAuth, className } = props;

  const { t } = useAppTranslation('booking');
  const [vkLogin] = useVkAuthMutation();
  const [createLead] = useCreateLeadMutation();

  const warehouseData = useAppSelector(getWarehouseInfo);
  const boxData = useAppSelector(getBoxInfo);

  useEffect(() => {
    console.log({
      app: Number(VK_AUTH_CLIENT_ID),
      redirectUrl: `${CLIENT_HOST}/login-process`,
      responseMode: VKID.ConfigResponseMode.Callback,
      source: VKID.ConfigSource.LOWCODE,
      scope: 'email',
    });

    VKID.Config.init({
      app: Number(VK_AUTH_CLIENT_ID),
      redirectUrl: `${CLIENT_HOST}/login-process`,
      responseMode: VKID.ConfigResponseMode.Callback,
      source: VKID.ConfigSource.LOWCODE,
      scope: 'email',
    });
  }, []);

  const handleLogin = async (provider: VKID.OAuthName): Promise<void> => {
    try {
      const result: CustomAny = await VKID.Auth.login({ provider });

      const { code, device_id: deviceId } = result;
      const data = await VKID.Auth.exchangeCode(code, deviceId);

      const accessToken = data.access_token;
      const response = await vkLogin(accessToken).unwrap();

      if (response?.email) {
        const leadData: LeadData = {
          email: response.email,
          square: boxData?.sizeCode?.square,
          warehouseId: warehouseData?.warehouseId,
          note: t('Lead signed in with VK'),
          isThirdPartyAuthorized: true,
        };

        await createLead(leadData);
      }

      onSuccessAuth();
    } catch (err: CustomAny) {
      console.error('VKID login error', err);
      onFailAuth(err?.data?.message || t('VK login failed'));
    }
  };

  return (
    <div className={className}>
      <Button
        size="medium-large"
        icon={<VkIcon className="w-[30px] h-[30px]" />}
        onClick={async () => {
          await handleLogin(VKID.OAuthName.VK);
        }}
        className="!bg-[#0077FF] !px-6"
      />
      <Button
        size="medium-large"
        icon={<MailRuIcon className="w-[30px] h-[30px]" />}
        onClick={async () => {
          await handleLogin(VKID.OAuthName.MAIL);
        }}
        className="!bg-[#005FF9] !px-6"
      />
      <Button
        size="medium-large"
        icon={<OkIcon className="w-[30px] h-[30px]" />}
        onClick={async () => {
          await handleLogin(VKID.OAuthName.OK);
        }}
        className="!bg-[#F79A38] !px-6"
      />
    </div>
  );
};
